class KlipCouponPrice {
  constructor(section) {
    this.section = section;
    this.updateQueued = false;
    this.couponDiscount = null;

    this.observer = new MutationObserver(() => this.queueUpdate());
    this.observer.observe(this.section, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    this.section.addEventListener('change', () => this.queueUpdate());

    this.update();
  }

  queueUpdate() {
    if (this.updateQueued) return;

    this.updateQueued = true;
    window.requestAnimationFrame(() => {
      this.updateQueued = false;
      this.update();
    });
  }

  getCouponDiscount() {
    const couponBoxes = this.section.querySelectorAll(
      '.elsklip-coupon-box, .elsklip-coupon__container'
    );

    if (couponBoxes.length === 0) {
      this.couponDiscount = null;
      return null;
    }

    let couponIsApplied = false;

    for (const couponBox of couponBoxes) {
      const checkbox = couponBox.querySelector('input[type="checkbox"]');
      if (checkbox?.checked) couponIsApplied = true;

      const discount = this.getDiscountFromText(couponBox.textContent);
      if (discount) this.couponDiscount = discount;
    }

    return couponIsApplied ? this.couponDiscount : null;
  }

  getDiscountFromText(text) {
    const percentageMatch = text.match(/(\d+(?:[.,]\d+)?)\s*%/);
    if (percentageMatch) {
      const percentage = Number(percentageMatch[1].replace(',', '.'));
      if (percentage > 0 && percentage < 100) {
        return { type: 'percentage', value: percentage };
      }
    }

    const fixedAmount = this.getFixedAmountFromText(text);
    if (fixedAmount) return { type: 'fixed', value: fixedAmount };

    return null;
  }

  getFixedAmountFromText(text) {
    const amountMatch = text.match(
      /(?:€|\$|£|EUR|USD|GBP)\s*(\d[\d.,]*)|(\d[\d.,]*)\s*(?:€|EUR|USD|GBP|\$|£)/i
    );
    if (!amountMatch) return null;

    const cents = this.moneyToCents(amountMatch[1] || amountMatch[2]);
    if (!Number.isFinite(cents) || cents <= 0) return null;

    return cents;
  }

  moneyToCents(amountText) {
    const value = amountText.trim();
    const lastComma = value.lastIndexOf(',');
    const lastDot = value.lastIndexOf('.');
    let normalized = value;

    if (lastComma > lastDot) {
      normalized = value.replace(/\./g, '').replace(',', '.');
    } else if (lastDot > lastComma) {
      normalized = value.replace(/,/g, '');
    }

    const amount = Number(normalized);
    if (!Number.isFinite(amount)) return null;

    return Math.round(amount * 100);
  }

  getPriceElement() {
    return this.section.querySelector('[id^="price-"] .price[data-price-cents]');
  }

  update() {
    const priceElement = this.getPriceElement();
    if (!priceElement) return;

    const discount = this.getCouponDiscount();
    if (!discount) {
      this.removeCouponPrice(priceElement);
      return;
    }

    const originalPrice = Number(priceElement.dataset.priceCents);
    if (!Number.isFinite(originalPrice)) return;

    const discountedPrice = this.calculateDiscountedPrice(originalPrice, discount);
    this.applyCouponPrice(priceElement, originalPrice, discountedPrice, discount);
  }

  calculateDiscountedPrice(originalPrice, discount) {
    if (discount.type === 'percentage') {
      const discountAmount = Math.floor(originalPrice * discount.value / 100);
      return Math.max(0, originalPrice - discountAmount);
    }

    return Math.max(0, originalPrice - discount.value);
  }

  applyCouponPrice(priceElement, originalPrice, discountedPrice, discount) {
    const state = `${originalPrice}-${discountedPrice}-${discount.type}-${discount.value}`;
    if (priceElement.dataset.klipState === state) return;

    const compareAtPrice = Number(priceElement.dataset.compareAtCents);
    const hadSale = compareAtPrice > originalPrice;
    const strikePrice = hadSale ? compareAtPrice : originalPrice;
    this.rememberOriginalPrice(priceElement, hadSale);

    const salePrice = priceElement.querySelector('.price__sale .price-item--sale');
    const strikePriceElement = priceElement.querySelector('.price__sale .price-item--regular');
    if (salePrice) salePrice.textContent = this.formatMoney(discountedPrice);
    if (strikePriceElement && !hadSale) {
      strikePriceElement.textContent = this.formatMoney(originalPrice);
    }

    priceElement.classList.add('price--on-sale', 'price--klip-coupon');
    priceElement.classList.toggle('price--klip-full-price', !hadSale);
    this.updateBadge(priceElement, strikePrice, discountedPrice);
    priceElement.dataset.klipState = state;
  }

  rememberOriginalPrice(priceElement, hadSale) {
    if (priceElement.dataset.klipReady === 'true') return;

    const salePrice = priceElement.querySelector('.price__sale .price-item--sale');
    const strikePrice = priceElement.querySelector('.price__sale .price-item--regular');
    const badge = priceElement.querySelector('.price__badge-sale');

    priceElement.dataset.klipReady = 'true';
    priceElement.dataset.klipHadSale = hadSale ? 'true' : 'false';
    priceElement.dataset.klipSaleText = salePrice ? salePrice.textContent : '';
    priceElement.dataset.klipStrikeText = strikePrice ? strikePrice.textContent : '';
    priceElement.dataset.klipBadgeText = badge ? badge.textContent.trim() : '';
  }

  updateBadge(priceElement, strikePrice, discountedPrice) {
    if (!priceElement.dataset.saleBadgeClass || strikePrice <= discountedPrice) return;

    let badge = priceElement.querySelector('.price__badge-sale');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = priceElement.dataset.saleBadgeClass;
      badge.dataset.klipBadge = 'true';
      const soldOutBadge = priceElement.querySelector('.price__badge-sold-out');
      priceElement.insertBefore(badge, soldOutBadge);
    }

    const savings = strikePrice - discountedPrice;
    if (priceElement.dataset.savingsInEuro === 'true') {
      badge.textContent = `Spare ${this.formatMoney(savings)}`;
      return;
    }

    const percent = Math.round((savings * 100) / strikePrice);
    badge.textContent = `${percent}% Rabatt`;
  }

  removeCouponPrice(priceElement) {
    priceElement.querySelector('.klip-coupon-price')?.remove();
    if (priceElement.dataset.klipReady !== 'true') return;

    const salePrice = priceElement.querySelector('.price__sale .price-item--sale');
    const strikePrice = priceElement.querySelector('.price__sale .price-item--regular');
    const badge = priceElement.querySelector('.price__badge-sale');

    if (salePrice) salePrice.textContent = priceElement.dataset.klipSaleText;
    if (strikePrice) strikePrice.textContent = priceElement.dataset.klipStrikeText;

    if (badge?.dataset.klipBadge === 'true') {
      badge.remove();
    } else if (badge) {
      badge.textContent = priceElement.dataset.klipBadgeText;
    }

    if (priceElement.dataset.klipHadSale !== 'true') {
      priceElement.classList.remove('price--on-sale');
    }

    priceElement.classList.remove('price--klip-coupon', 'price--klip-full-price');
    delete priceElement.dataset.klipReady;
    delete priceElement.dataset.klipHadSale;
    delete priceElement.dataset.klipSaleText;
    delete priceElement.dataset.klipStrikeText;
    delete priceElement.dataset.klipBadgeText;
    delete priceElement.dataset.klipState;
  }

  formatMoney(cents) {
    const format = this.section.dataset.moneyFormat || '{{amount}}';
    const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholderMatch) return (cents / 100).toFixed(2);

    const placeholder = placeholderMatch[1];
    let value;

    if (placeholder === 'amount_no_decimals') {
      value = this.formatNumber(cents, 0, ',', '.');
    } else if (placeholder === 'amount_with_comma_separator') {
      value = this.formatNumber(cents, 2, '.', ',');
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      value = this.formatNumber(cents, 0, '.', ',');
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      value = this.formatNumber(cents, 2, "'", '.');
    } else {
      value = this.formatNumber(cents, 2, ',', '.');
    }

    return format.replace(placeholderMatch[0], value);
  }

  formatNumber(cents, decimals, thousandsSeparator, decimalSeparator) {
    const value = (cents / 100).toFixed(decimals);
    const parts = value.split('.');

    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    return decimals === 0 ? parts[0] : `${parts[0]}${decimalSeparator}${parts[1]}`;
  }
}

document.querySelectorAll('[data-klip-coupon-price]').forEach((section) => {
  new KlipCouponPrice(section);
});
